import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';

// ── SHARED TIME HELPERS (also used by the admin page) ─────────
export const isTimeclockUser = (u) => !!u && u.type === 'inhouse' && u.rate_unit === 'hr' && u.role !== 'owner';

export function fmtHrs(h) {
  const mins = Math.round((Number(h) || 0) * 60);
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}
export const fmtMins = (m) => {
  const n = Math.max(0, Math.round(Number(m) || 0));
  return n < 60 ? `${n}m` : `${Math.floor(n / 60)}h ${String(n % 60).padStart(2, '0')}m`;
};
export const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' }) : '—';
export const fmtDay = (ymd) => new Date(ymd + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
export const addDays = (ymd, n) => { const d = new Date(ymd + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export const jobLabel = (p) => p.job_id ? `${p.job_number ? p.job_number + ' · ' : ''}${p.job_title || 'Job'}` : 'General / Shop';

// "YYYY-MM-DDTHH:MM" in the device's local time, for datetime-local inputs
export function toLocalInput(d) {
  if (!d) return '';
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

// Ask the phone for a location; resolve with nulls instead of failing
function getPosition() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });
}

export function JobSelect({ jobs, value, onChange, style }) {
  const mine = jobs.filter(j => j.assigned);
  const other = jobs.filter(j => !j.assigned);
  const label = (j) => `${j.job_number ? j.job_number + ' · ' : ''}${j.title}`;
  return (
    <select className="fs" value={value || ''} onChange={e => onChange(e.target.value || null)} style={style}>
      <option value="">General / Shop (no job)</option>
      {mine.length > 0 && <optgroup label="My jobs">{mine.map(j => <option key={j.id} value={j.id}>{label(j)}</option>)}</optgroup>}
      {mine.length > 0 && other.length > 0 && <optgroup label="Other jobs">{other.map(j => <option key={j.id} value={j.id}>{label(j)}</option>)}</optgroup>}
      {mine.length === 0 && other.map(j => <option key={j.id} value={j.id}>{label(j)}</option>)}
    </select>
  );
}

const card = { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--r2)' };
const sectionHd = { fontSize: 11, fontWeight: 600, color: 'var(--tx2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.07em' };

// ── CREW TIME CLOCK ───────────────────────────────────────────
export default function TimeClockField() {
  const [status, setStatus] = useState(null);
  const [week, setWeek] = useState(null);
  const [weekStart, setWeekStart] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [fix, setFix] = useState(null);       // correction modal: { punch } or { punch: null }
  const [askBreak, setAskBreak] = useState(false); // clock-out break prompt
  const offset = useRef(0);
  const loadedAt = useRef(Date.now());

  const loadStatus = useCallback(async () => {
    const r = await api.get('/time/status');
    offset.current = new Date(r.data.serverTime || Date.now()) - Date.now();
    loadedAt.current = Date.now();
    setStatus(r.data);
    return r.data;
  }, []);

  const loadWeek = useCallback(async (ws) => {
    const r = await api.get('/time/my', { params: ws ? { weekStart: ws } : {} });
    setWeek(r.data);
    setWeekStart(r.data.weekStart);
  }, []);

  useEffect(() => {
    loadStatus().then(s => { if (s.eligible) loadWeek(); }).catch(() => setMsg({ err: 'Could not load the time clock' }));
  }, [loadStatus, loadWeek]);

  // Tick while clocked in — the break timer needs it too
  useEffect(() => {
    if (!status?.open) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [status?.open]);

  // Default the job picker to the open punch's job, else the first assigned job
  useEffect(() => {
    if (!status?.eligible) return;
    if (status.open) setJobId(status.open.job_id || null);
    else setJobId(prev => prev ?? (status.jobs.find(j => j.assigned)?.id || null));
  }, [status]);

  async function punch(kind, extra = {}) {
    setBusy(kind); setMsg(null);
    try {
      let loc = null;
      if (status.gpsEnabled) loc = await getPosition();
      const body = { ...(loc || {}), ...extra, ...(kind === 'out' ? {} : { jobId }) };
      const r = await api.post(kind === 'out' ? '/time/clock-out' : '/time/clock-in', body);
      let okText = kind === 'out' ? 'Clocked out' : r.data.switched ? 'Switched jobs' : 'Clocked in';
      if (kind === 'out' && r.data.breakMinutes > 0) {
        okText += ` · ${fmtMins(r.data.breakMinutes)} unpaid break`;
        if (r.data.breakSource === 'auto') okText += ' (automatic)';
      }
      setMsg({ ok: okText + (status.gpsEnabled && !loc ? ' (location not captured — check location permission)' : '') });
      await Promise.all([loadStatus(), loadWeek()]);
    } catch (e) {
      setMsg({ err: e.response?.data?.error || 'Something went wrong — try again' });
    } finally {
      setBusy('');
    }
  }

  async function breakAction(which) {
    setBusy('break'); setMsg(null);
    try {
      const r = await api.post(`/time/break/${which}`, {});
      setMsg({ ok: which === 'start' ? 'Break started — the clock is paused'
                                     : `Break ended · ${fmtMins(r.data.breakMinutes)} unpaid today` });
      await Promise.all([loadStatus(), loadWeek()]);
    } catch (e) {
      setMsg({ err: e.response?.data?.error || 'Could not update your break' });
    } finally {
      setBusy('');
    }
  }

  // Clock out: if they never touched the break button, ask before closing out
  function clockOut() {
    const usedTimer = (status.open?.break_count || 0) > 0 || status.onBreak;
    if (usedTimer) return punch('out');
    // Punched in by mistake and straight back out — nothing to ask about.
    const minsOnClock = open ? (srvNow - new Date(open.clock_in)) / 60000 : 0;
    if (minsOnClock < 2) return punch('out');
    setAskBreak(true);
  }

  async function cancelFix(id) {
    if (!window.confirm('Cancel this fix request?')) return;
    try { await api.delete(`/time/corrections/${id}`); loadWeek(weekStart); }
    catch (e) { alert(e.response?.data?.error || 'Could not cancel'); }
  }

  if (!status) return <div className="empty"><div className="spin" style={{ margin: '0 auto 12px' }}></div>Loading…</div>;
  if (!status.eligible) return <div className="empty"><i className="fa fa-clock"></i>The time clock is for hourly in-house staff.</div>;

  const open = status.open;
  const onBreak = !!status.onBreak;
  const srvNow = now + offset.current;

  // Paid time = elapsed minus banked break minus any break running right now
  const paidAt = (t) => {
    if (!open) return 0;
    const bankedMin = Number(open.break_minutes) || 0;
    const runningMin = open.break_started_at
      ? Math.max(0, (t - new Date(open.break_started_at)) / 60000) : 0;
    return Math.max(0, (t - new Date(open.clock_in)) / 3600000 - (bankedMin + runningMin) / 60);
  };
  const elapsed = paidAt(srvNow);
  const breakRunningMin = onBreak ? Math.max(0, (srvNow - new Date(open.break_started_at)) / 60000) : 0;
  const breakTotalMin = open ? (Number(open.break_minutes) || 0) + breakRunningMin : 0;

  const hms = (h) => { const s = Math.floor(h * 3600); return [Math.floor(s / 3600), Math.floor(s / 60) % 60, s % 60].map((n, i) => i ? String(n).padStart(2, '0') : n).join(':'); };
  const mmss = (m) => { const s = Math.floor(m * 60); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
  const sameJob = open && (open.job_id || null) === (jobId || null);

  // Server totals were correct at load; add only the paid time earned since
  const live = open ? Math.max(0, paidAt(srvNow) - paidAt(loadedAt.current + offset.current)) : 0;
  const todayTotal = status.today + live;
  const weekTotal = status.week.total + live;

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      {/* Clock card */}
      <div style={{ ...card, borderLeft: `3px solid ${onBreak ? 'var(--amber)' : open ? 'var(--green)' : 'var(--border2, var(--border))'}`, padding: '16px 16px 14px', marginBottom: 12 }}>
        {open ? (
          <>
            {onBreak ? (
              <div style={{ fontSize: 11, color: 'var(--amber2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                <i className="fa fa-mug-hot"></i> On break — clock paused
              </div>
            ) : (
              <div style={{ fontSize: 11, color: 'var(--green2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                <i className="fa fa-circle" style={{ fontSize: 8, verticalAlign: 'middle' }}></i> On the clock
              </div>
            )}

            <div style={{ fontSize: 38, fontWeight: 600, fontFamily: "'DM Mono',monospace", letterSpacing: '-.02em', margin: '4px 0 2px', color: onBreak ? 'var(--tx3)' : 'var(--tx)' }}>
              {hms(elapsed)}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{jobLabel(open)}</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: onBreak ? 10 : 14 }}>
              Since {fmtTime(open.clock_in)}
              {breakTotalMin >= 1 && <> · {fmtMins(breakTotalMin)} unpaid break</>}
            </div>

            {onBreak && (
              <div style={{ background: 'rgba(232,160,32,.1)', border: '1px solid rgba(232,160,32,.25)', borderRadius: 'var(--r)', padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <i className="fa fa-mug-hot" style={{ color: 'var(--amber2)' }}></i>
                <div style={{ flex: 1, fontSize: 12, color: 'var(--amber2)' }}>Break running</div>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 15, fontWeight: 600, color: 'var(--amber2)' }}>{mmss(breakRunningMin)}</div>
              </div>
            )}

            {onBreak ? (
              <button className="btn btn-green btn-lg" style={{ width: '100%', justifyContent: 'center', padding: '14px 0', fontSize: 15 }}
                disabled={!!busy} onClick={() => breakAction('end')}>
                {busy === 'break' ? <div className="spin" style={{ width: 16, height: 16, borderWidth: 2 }}></div> : <i className="fa fa-play"></i>} End break — back to work
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 7 }}>
                <button className="btn btn-red btn-lg" style={{ flex: 1, justifyContent: 'center', padding: '14px 0', fontSize: 15 }}
                  disabled={!!busy} onClick={clockOut}>
                  {busy === 'out' ? <div className="spin" style={{ width: 16, height: 16, borderWidth: 2 }}></div> : <i className="fa fa-stop-circle"></i>} Clock out
                </button>
                <button className="btn btn-lg" style={{ justifyContent: 'center', padding: '14px 16px', fontSize: 15, background: 'rgba(232,160,32,.12)', color: 'var(--amber2)', border: '1px solid rgba(232,160,32,.3)' }}
                  disabled={!!busy} onClick={() => breakAction('start')}>
                  {busy === 'break' ? <div className="spin" style={{ width: 16, height: 16, borderWidth: 2 }}></div> : <i className="fa fa-mug-hot"></i>} Break
                </button>
              </div>
            )}

            {!onBreak && (
              <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
                <JobSelect jobs={status.jobs} value={jobId} onChange={setJobId} style={{ flex: 1, minWidth: 0 }} />
                <button className="btn btn-blue" disabled={!!busy || sameJob} onClick={() => punch('switch')} style={{ whiteSpace: 'nowrap' }}>
                  {busy === 'switch' ? <div className="spin" style={{ width: 14, height: 14, borderWidth: 2 }}></div> : <i className="fa fa-right-left"></i>} Switch job
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ fontSize: 11, color: 'var(--tx3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 10 }}>
              Off the clock
            </div>
            <label className="fl">Job</label>
            <JobSelect jobs={status.jobs} value={jobId} onChange={setJobId} style={{ width: '100%', marginBottom: 12 }} />
            <button className="btn btn-green btn-lg" style={{ width: '100%', justifyContent: 'center', padding: '14px 0', fontSize: 15 }}
              disabled={!!busy} onClick={() => punch('in')}>
              {busy === 'in' ? <div className="spin" style={{ width: 16, height: 16, borderWidth: 2 }}></div> : <i className="fa fa-play-circle"></i>} Clock in
            </button>
          </>
        )}
        {status.gpsEnabled && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 9 }}><i className="fa fa-location-dot"></i> Your location is recorded when you clock in and out</div>}
        {msg && (
          <div style={{ fontSize: 12, marginTop: 10, padding: '8px 10px', borderRadius: 'var(--r)',
            color: msg.err ? 'var(--red2)' : 'var(--green2)', background: msg.err ? 'rgba(231,76,60,.1)' : 'rgba(34,160,107,.1)' }}>
            <i className={`fa ${msg.err ? 'fa-exclamation-circle' : 'fa-check-circle'}`}></i> {msg.err || msg.ok}
          </div>
        )}
      </div>

      {/* Totals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 16 }}>
        {[
          ['Today', fmtHrs(todayTotal), 'var(--tx)'],
          ['This week', fmtHrs(weekTotal), 'var(--blue2)'],
          ['Overtime', fmtHrs(status.week.overtime), status.week.overtime > 0 ? 'var(--amber2)' : 'var(--tx3)'],
          ['Breaks', fmtMins(status.week.breakMinutes || 0), 'var(--tx3)'],
        ].map(([l, v, c]) => (
          <div key={l} style={{ ...card, padding: '11px 12px' }}>
            <div style={{ fontSize: 10, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{l}</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Week punches */}
      {week && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <button className="btn btn-sm" onClick={() => loadWeek(addDays(weekStart, -7))}><i className="fa fa-chevron-left"></i></button>
            <div style={{ flex: 1, textAlign: 'center', fontSize: 12, fontWeight: 600 }}>
              {fmtDay(week.weekStart)} – {fmtDay(week.weekEnd)}
              <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tx3)' }}>
                {fmtHrs(week.total)}{week.overtime > 0 ? ` · ${fmtHrs(week.overtime)} OT` : ''}
                {week.breakMinutes > 0 ? ` · ${fmtMins(week.breakMinutes)} break` : ''}
              </div>
            </div>
            <button className="btn btn-sm" disabled={weekStart === status.week.start} onClick={() => loadWeek(addDays(weekStart, 7))}><i className="fa fa-chevron-right"></i></button>
          </div>

          {week.punches.length === 0 && <div style={{ ...card, padding: 14, fontSize: 12, color: 'var(--tx3)', textAlign: 'center', marginBottom: 8 }}>No time this week</div>}

          {Object.keys(week.days).sort().reverse().map(day => (
            <div key={day} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--tx2)', fontWeight: 600, margin: '0 2px 4px' }}>
                <span>{fmtDay(day)}</span><span>{fmtHrs(week.days[day])}</span>
              </div>
              {week.punches.filter(p => p.work_date === day).reverse().map(p => {
                const pendingFix = week.corrections.find(c => c.punch_id === p.id && c.status === 'pending');
                return (
                  <div key={p.id} style={{ ...card, padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5, borderLeft: `3px solid ${p.on_break ? 'var(--amber)' : p.open ? 'var(--green)' : 'var(--blue)'}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{jobLabel(p)}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx3)' }}>
                        {fmtTime(p.clock_in)} – {p.open ? 'now' : fmtTime(p.clock_out)} · {fmtHrs(p.hours)}
                        {p.breakMinutes > 0 && <span style={{ color: 'var(--amber2)' }}> · {fmtMins(p.breakMinutes)} break</span>}
                        {p.source !== 'clock' && <span className="tag tag-gray" style={{ fontSize: 9, marginLeft: 6 }}>edited</span>}
                      </div>
                    </div>
                    {pendingFix
                      ? <span className="tag tag-amber" style={{ fontSize: 10 }}>Fix pending</span>
                      : (!p.open || p.hours > 12) && <button className="btn btn-sm" onClick={() => setFix({ punch: p })}><i className="fa fa-pen"></i> Fix</button>}
                  </div>
                );
              })}
            </div>
          ))}

          <button className="btn" style={{ width: '100%', justifyContent: 'center', marginBottom: 18 }} onClick={() => setFix({ punch: null })}>
            <i className="fa fa-plus"></i> Missed a punch? Add time
          </button>

          {week.corrections.length > 0 && (
            <>
              <div style={sectionHd}>My fix requests</div>
              {week.corrections.map(c => (
                <div key={c.id} style={{ ...card, padding: '9px 12px', marginBottom: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{c.punch_id ? 'Fix' : 'Missed punch'} · {fmtDay(toLocalInput(c.clock_in).slice(0, 10))}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx3)' }}>
                        {fmtTime(c.clock_in)} – {fmtTime(c.clock_out)}
                        {c.break_minutes > 0 ? ` · ${fmtMins(c.break_minutes)} break` : ''} · {jobLabel(c)}
                      </div>
                    </div>
                    <span className={`tag tag-${c.status === 'approved' ? 'green' : c.status === 'rejected' ? 'red' : 'amber'}`} style={{ fontSize: 10, textTransform: 'capitalize' }}>{c.status}</span>
                    {c.status === 'pending' && <button className="btn btn-sm" title="Cancel request" onClick={() => cancelFix(c.id)}><i className="fa fa-times"></i></button>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 4 }}>“{c.reason}”</div>
                  {c.review_note && <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>Office: {c.review_note}</div>}
                </div>
              ))}
            </>
          )}
        </>
      )}

      {askBreak && (
        <BreakPrompt
          elapsedHours={elapsed}
          max={status.breakRules?.maxMinutes || 480}
          onCancel={() => setAskBreak(false)}
          onConfirm={(mins) => { setAskBreak(false); punch('out', { breakMinutes: mins }); }}
        />
      )}

      {fix && (
        <FixModal punch={fix.punch} jobs={status.jobs} max={status.breakRules?.maxMinutes || 480}
          onClose={() => setFix(null)}
          onSaved={() => { setFix(null); setMsg({ ok: 'Fix sent to the office for approval' }); loadWeek(weekStart); }} />
      )}
    </div>
  );
}

// ── BREAK PROMPT ON CLOCK OUT ─────────────────────────────────
// Only shown when the break button was never used on this punch.
function BreakPrompt({ elapsedHours, max, onCancel, onConfirm }) {
  const [mins, setMins] = useState(0);
  const [custom, setCustom] = useState('');
  const shiftMins = Math.max(0, Math.floor(elapsedHours * 60));
  const chosen = custom !== '' ? Math.max(0, Math.round(Number(custom) || 0)) : mins;
  // No break is always valid — only a positive break can exceed the shift.
  // Without the chosen > 0 guard, a shift under a minute makes shiftMins 0 and
  // even "None" reads as too long, which disables the Clock out button.
  const tooLong = chosen > 0 && chosen >= shiftMins;
  const cap = Math.max(0, Math.min(max, shiftMins));

  return (
    <div className="overlay" onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div className="mhd">
          <div style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>Any unpaid break?</div>
          <button className="btn btn-sm" style={{ padding: '4px 7px' }} onClick={onCancel}><i className="fa fa-times"></i></button>
        </div>
        <div className="mbody">
          <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 13 }}>
            You didn't use the break button on this shift. {fmtHrs(elapsedHours)} on the clock so far.
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {[0, 15, 30, 60].filter(v => v === 0 || v < shiftMins).map(v => (
              <button key={v} className={`btn ${chosen === v && custom === '' ? 'btn-blue' : ''}`}
                onClick={() => { setMins(v); setCustom(''); }} style={{ flex: '1 1 auto', justifyContent: 'center' }}>
                {v === 0 ? 'None' : `${v} min`}
              </button>
            ))}
          </div>
          <div className="fg" style={{ marginBottom: 6 }}>
            <label className="fl">Or enter minutes</label>
            <input type="number" min="0" max={cap} className="fi" value={custom}
              onChange={e => setCustom(e.target.value)} placeholder="e.g. 45" style={{ width: 130 }} />
          </div>
          {tooLong && (
            <div style={{ fontSize: 12, color: 'var(--red2)', padding: '8px 10px', background: 'rgba(231,76,60,.1)', borderRadius: 'var(--r)' }}>
              <i className="fa fa-exclamation-circle"></i> That's as long as the shift. Send a fix request instead.
            </div>
          )}
        </div>
        <div className="mfoot">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-red" disabled={tooLong} onClick={() => onConfirm(chosen)}>
            <i className="fa fa-stop-circle"></i> Clock out{chosen > 0 ? ` · −${chosen}m` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── FIX / MISSED PUNCH MODAL ──────────────────────────────────
function FixModal({ punch, jobs, max, onClose, onSaved }) {
  const [jobId, setJobId] = useState(punch ? punch.job_id || null : (jobs.find(j => j.assigned)?.id || null));
  const [start, setStart] = useState(punch ? toLocalInput(punch.clock_in) : '');
  const [end, setEnd] = useState(punch && punch.clock_out ? toLocalInput(punch.clock_out) : '');
  const [brk, setBrk] = useState(punch ? String(Math.round(Number(punch.breakMinutes) || 0)) : '0');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const spanMins = start && end && new Date(end) > new Date(start)
    ? Math.floor((new Date(end) - new Date(start)) / 60000) : null;
  const brkNum = Math.max(0, Math.round(Number(brk) || 0));

  async function save() {
    setErr('');
    if (!start || !end) return setErr('Enter a start and end time');
    if (new Date(end) <= new Date(start)) return setErr('End time must be after start time');
    if (brkNum > max) return setErr(`Break cannot be more than ${fmtMins(max)}`);
    if (spanMins !== null && brkNum >= spanMins) return setErr('The break cannot be as long as the shift');
    if (!reason.trim()) return setErr('Tell the office why');
    setSaving(true);
    try {
      await api.post('/time/corrections', {
        punchId: punch?.id, jobId,
        clockIn: new Date(start).toISOString(), clockOut: new Date(end).toISOString(),
        breakMinutes: brkNum,
        reason: reason.trim(),
      });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not send request');
    } finally {
      setSaving(false);
    }
  }

  const paid = spanMins !== null ? Math.max(0, spanMins - brkNum) / 60 : null;

  return (
    <div className="overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="mhd">
          <div style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{punch ? 'Fix a punch' : 'Add missed time'}</div>
          <button className="btn btn-sm" style={{ padding: '4px 7px' }} onClick={onClose}><i className="fa fa-times"></i></button>
        </div>
        <div className="mbody">
          {punch && (
            <div style={{ fontSize: 11, color: 'var(--tx3)', background: 'var(--bg3)', borderRadius: 'var(--r)', padding: '8px 10px', marginBottom: 12 }}>
              Currently: {fmtTime(punch.clock_in)} – {punch.open ? 'still clocked in' : fmtTime(punch.clock_out)}
              {punch.breakMinutes > 0 ? ` · ${fmtMins(punch.breakMinutes)} break` : ''} · {jobLabel(punch)}
            </div>
          )}
          <div className="fg"><label className="fl">Job</label><JobSelect jobs={jobs} value={jobId} onChange={setJobId} style={{ width: '100%' }} /></div>
          <div className="fg"><label className="fl">Start</label><input type="datetime-local" className="fi" value={start} onChange={e => setStart(e.target.value)} /></div>
          <div className="fg"><label className="fl">End</label><input type="datetime-local" className="fi" value={end} onChange={e => setEnd(e.target.value)} /></div>
          <div className="fg">
            <label className="fl">Unpaid break (minutes)</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="number" min="0" className="fi" value={brk} onChange={e => setBrk(e.target.value)} style={{ width: 110 }} />
              {[0, 30, 60].map(v => (
                <button key={v} className="btn btn-sm" onClick={() => setBrk(String(v))} style={{ color: 'var(--tx3)' }}>{v}</button>
              ))}
            </div>
          </div>
          {paid !== null && (
            <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: -4, marginBottom: 10 }}>
              {fmtHrs(spanMins / 60)} on site{brkNum > 0 ? ` − ${fmtMins(brkNum)} break` : ''} = <strong style={{ color: 'var(--tx2)' }}>{fmtHrs(paid)} paid</strong>
            </div>
          )}
          <div className="fg"><label className="fl">Reason</label><textarea className="fi fta" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Forgot to clock out" /></div>
          {err && <div style={{ fontSize: 12, color: 'var(--red2)', padding: '8px 10px', background: 'rgba(231,76,60,.1)', borderRadius: 'var(--r)' }}><i className="fa fa-exclamation-circle"></i> {err}</div>}
        </div>
        <div className="mfoot">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-blue" onClick={save} disabled={saving}>
            {saving ? <><div className="spin" style={{ width: 14, height: 14, borderWidth: 2 }}></div> Sending…</> : <><i className="fa fa-paper-plane"></i> Send for approval</>}
          </button>
        </div>
      </div>
    </div>
  );
}
